import React from 'react'
import { observer } from 'mobx-react'
import {
  LocationOption,
  PrimarySoftwareOption,
  SpecialtyOption,
  SupplementalSoftwareOption,
} from 'admin/staffing/dtos/payloads'
import { ActionToggle } from 'components/ActionToggle'
import Avatar from 'components/Avatar'
import Button from 'components/Button'
// import Checkbox from 'components/Checkbox'
import {
  CustomSelect,
  ItemSource,
  useCustomSelectStateFromItemSource,
} from 'components/inputs/CustomSelect'
import {
  Form,
  FormGroupContainer,
  FormRow,
  NumericInputContainer,
  TextAreaContainer,
} from 'components/inputs'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetSection,
  ModalSheetTitle,
} from 'components/ModalSheet'
import { RadioButtonGroup, RadioButtonItem } from 'components/RadioButtonGroup'
import Workspace from 'iam/models/Workspace'
import { SingleItemSelector, SelectItem } from './SingleItemSelector'
import { WorkspacePostingDialogViewModel } from './WorkspacePostingDialogViewModel'
import styles from './WorkspacePostingDialog.sass'
import { capitalizeFirstLetter } from 'utils/string-util'

interface WorkspacePostingFormProps {
  vm: WorkspacePostingDialogViewModel
  title: string
  buttonText: string
}

export const WorkspacePostingForm = observer(function ({
  vm,
  title,
  buttonText,
}: WorkspacePostingFormProps) {
  return (
    <ModalSheet
      showing={vm.showing}
      onRequestClose={vm.close}
      disableCloseOnOutsideClick
    >
      <ModalSheetHeader bordered withCloseButton>
        <ModalSheetTitle>{title}</ModalSheetTitle>
      </ModalSheetHeader>
      <ModalSheetContent>
        <ModalSheetSection spaced>
          <Form id="postingDialog" onSubmit={vm.submit}>
            <FormRow>
              <FormGroupContainer label="Select workspace">
                <SingleItemSelector
                  model={vm.workspace}
                  itemOptions={vm.workspaces}
                  itemToString={(option?: Workspace) =>
                    option ? option.name : 'Select a workspace'
                  }
                  itemPrefix={(item: Workspace) => (
                    <Avatar
                      size={24}
                      subject={{
                        displayName: item.name,
                        avatar: item.branding.logos.icon,
                      }}
                    />
                  )}
                  onChange={React.useCallback(
                    (workspace: Workspace | undefined) =>
                      vm.setWorkspace(workspace || null),
                    [vm]
                  )}
                  disabled={
                    Boolean(vm.workspacePosting?.id) || vm.submit.pending
                  }
                />
              </FormGroupContainer>
            </FormRow>

            <FormRow>
              <FormGroupContainer label="Location">
                <SingleItemSelector
                  model={vm.location}
                  itemOptions={vm.locationItems}
                  itemToString={(item: LocationOption) => item}
                  onChange={React.useCallback(
                    (location: LocationOption | undefined) =>
                      vm.setLocation(location || null),
                    [vm]
                  )}
                  disabled={vm.submit.pending}
                />
              </FormGroupContainer>
            </FormRow>

            <FormRow>
              <FormGroupContainer label="Primary software">
                <SingleItemSelector
                  model={vm.primarySoftware}
                  itemOptions={vm.primarySoftwareItems}
                  itemToString={(item: PrimarySoftwareOption) => item}
                  onChange={React.useCallback(
                    (primarySoftware: PrimarySoftwareOption | undefined) =>
                      vm.setPrimarySoftware(primarySoftware || null),
                    [vm]
                  )}
                  disabled={vm.submit.pending}
                />
              </FormGroupContainer>
            </FormRow>

            <FormRow>
              <ActionToggle
                label="Backup required"
                description="Enable if the primary software requires a manual backup."
                value={vm.backupRequired}
                onChange={() => vm.setBackupRequired(!vm.backupRequired)}
                isThemed
              />
            </FormRow>

            <FormRow>
              <FormGroupContainer label="Supplemental software">
                <SingleItemSelector
                  model={vm.supplementalSoftware}
                  itemOptions={vm.supplementalSoftwareItems}
                  itemToString={(item: SupplementalSoftwareOption) => item}
                  onChange={React.useCallback(
                    (
                      supplementalSoftware:
                        | SupplementalSoftwareOption
                        | undefined
                    ) =>
                      vm.setSupplementalSoftware(supplementalSoftware || null),
                    [vm]
                  )}
                />
              </FormGroupContainer>
            </FormRow>

            <FormRow>
              <FormGroupContainer label="Specialties">
                <SpecialtiesSelector vm={vm} disabled={vm.submit.pending} />
              </FormGroupContainer>
            </FormRow>

            <FormRow>
              <div className={styles.numericInputContainerGroup}>
                <NumericInputContainer
                  label="Pros required"
                  field="prosRequired"
                  model={vm}
                  error={vm.validation.getError('prosRequired')}
                />
                {/* TODO: Add max applicants back in when it is supported. */}
                {/* <NumericInputContainer
                  label="Max applicants"
                  field="maxApplicants"
                  model={vm}
                  error={vm.validation.getError('maxApplicants')}
                /> */}
                <NumericInputContainer
                  label="Max concurrent workspaces"
                  field="maxConcurrentWorkspaces"
                  model={vm}
                  error={vm.validation.getError('maxConcurrentWorkspaces')}
                />
              </div>
            </FormRow>

            <FormRow>
              <RadioButtonGroup label="Type of work">
                {vm.jobTypeItems.map((type) => (
                  <RadioButtonItem
                    active={vm.typeOfWork.includes(type)}
                    onClick={() => vm.toggleTypeOfWork(type)}
                    key={type}
                  >
                    {capitalizeFirstLetter(type)}
                  </RadioButtonItem>
                ))}
              </RadioButtonGroup>
            </FormRow>

            <FormRow>
              <TextAreaContainer
                label="Description"
                field="description"
                model={vm}
                rows={4}
                minLength={1}
                maxLength={1024}
                error={vm.validation.getError('description')}
                help="Max 1024 characters."
              />
            </FormRow>
          </Form>
        </ModalSheetSection>
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button type="default" onClick={vm.close}>
            Cancel
          </Button>
          <Button
            submit
            form="postingDialog"
            type="primary"
            disabled={vm.workspace === Workspace.none || vm.submit.pending}
          >
            {buttonText}
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})

interface MultiItemSelectorProps {
  vm: WorkspacePostingDialogViewModel
  disabled?: boolean
  dataTest?: string
}

const SpecialtiesSelector = observer(function ({
  vm,
  disabled,
  dataTest,
}: MultiItemSelectorProps) {
  const itemSource = React.useMemo(
    () =>
      ItemSource.static(vm.specialtyItems, (item, text) =>
        item.toLowerCase().includes(text.toLowerCase())
      ),
    [vm.specialtyItems]
  )
  const change = React.useCallback(
    (specialties: SpecialtyOption[]) => vm.setSpecialties(specialties),
    [vm]
  )
  const selectProps = useCustomSelectStateFromItemSource({
    itemSource,
  })

  return (
    <CustomSelect
      {...selectProps}
      value={vm.specialties}
      onChange={change}
      multi={true}
      disabled={disabled}
      data-test={dataTest}
      renderSearchItem={({ item, ...itemProps }) => (
        <SelectItem
          // prefix={<Checkbox checked={itemProps.selected} size={24} />}
          {...itemProps}
        >
          {item}
        </SelectItem>
      )}
    />
  )
})
